import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, handleApiError, handleApiErrorDetailed } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [canRequestUnlock, setCanRequestUnlock] = useState(false);

  // Check if user is authenticated on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Token is stored in httpOnly cookie (sent automatically).
      // We verify by calling the /verify endpoint which reads the cookie.
      const response = await authAPI.verifyToken();

      if (response.data.valid) {
        setIsAuthenticated(true);
        setAdmin(response.data.admin);
      } else {
        // Token invalid — clear state only (cookie is handled server-side).
        setIsAuthenticated(false);
        setAdmin(null);
      }
    } catch (error) {
      // Cookie missing or invalid — user is not authenticated.
      setIsAuthenticated(false);
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      setIsRateLimited(false);
      setCanRequestUnlock(false);
      setLoading(true);

      const response = await authAPI.login(credentials);
      const { admin: adminData } = response.data;

      // Token is set as httpOnly cookie by the server — no localStorage.
      setIsAuthenticated(true);
      setAdmin(adminData);

      return { success: true };
    } catch (error) {
      const errorDetails = handleApiErrorDetailed(error);
      setError(errorDetails.message);
      setIsRateLimited(errorDetails.isRateLimited);
      setCanRequestUnlock(errorDetails.canRequestUnlock);
      return {
        success: false,
        error: errorDetails.message,
        isRateLimited: errorDetails.isRateLimited,
        canRequestUnlock: errorDetails.canRequestUnlock
      };
    } finally {
      setLoading(false);
    }
  };

  const requestUnlock = async (username) => {
    try {
      setLoading(true);
      const response = await authAPI.requestUnlock(username);
      return { success: true, message: response.data.message };
    } catch (error) {
      const errorMessage = handleApiError(error);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Server clears the httpOnly cookie and invalidates session.
      await authAPI.logout();
    } catch (error) {
      // Continue with local logout even if API call fails.
    } finally {
      setIsAuthenticated(false);
      setAdmin(null);
      setError(null);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setError(null);

      const response = await authAPI.updateProfile(profileData);
      const updatedAdmin = response.data.admin;

      setAdmin(updatedAdmin);

      return { success: true };
    } catch (error) {
      const errorMessage = handleApiError(error);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const clearError = () => {
    setError(null);
    setIsRateLimited(false);
    setCanRequestUnlock(false);
  };

  const hasPermission = (permission) => {
    if (!admin) return false;
    if (admin.role === 'super_admin') return true;
    return admin.permissions?.includes(permission) || false;
  };

  const value = {
    isAuthenticated,
    admin,
    loading,
    error,
    isRateLimited,
    canRequestUnlock,
    login,
    logout,
    updateProfile,
    requestUnlock,
    clearError,
    hasPermission,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
